import { z } from 'zod';

const AmountRawSchema = z.string().regex(/^\d+$/).transform(BigInt);

export const QuoteSchema = z.object({
  network: z.string(),
  provider: z.string(),
  fromToken: z.string(),
  toToken: z.string(),
  fromAmount: AmountRawSchema,
  toAmount: AmountRawSchema,
  priceImpactPct: z.number(),
  gasEstimate: z.string(),
  feeBps: z.number().int(),
  gaslessEligible: z.boolean(),
});

export type Quote = z.infer<typeof QuoteSchema>;

export const TradeSchema = z.object({
  id: z.string(),
  network: z.string(),
  toNetwork: z.string().nullish(),
  provider: z.string(),
  fromToken: z.string(),
  toToken: z.string(),
  fromAmount: AmountRawSchema,
  toAmount: AmountRawSchema,
  feeAmount: z.string(),
  feeBps: z.number().int(),
  txHash: z.string().nullable(),
  createdAt: z.string(),
});

export const TradeHistorySchema = z.object({
  data: z.array(TradeSchema),
  nextCursor: z.string().optional(),
  hasMore: z.boolean(),
});

export type Trade = z.infer<typeof TradeSchema>;
export type TradeHistory = z.infer<typeof TradeHistorySchema>;

export interface QuoteParams {
  fromToken: string;
  toToken: string;
  amount: string;
  network: string;
  slippageBps?: number;
}

export function quoteKey(p: QuoteParams | null): string {
  return p ? `${p.network}|${p.fromToken}|${p.toToken}|${p.amount}|${p.slippageBps ?? ''}` : '';
}

export interface ExecuteSwapParams extends QuoteParams {
  userAddress: string;
  referrerAddress?: string;
  gasless?: boolean;
}

export const NetworkFeeSchema = z.object({
  amountRaw: AmountRawSchema,
  decimals: z.number().int().nonnegative(),
  approveRequired: z.boolean(),
  isUpperBound: z.boolean(),
});

export type NetworkFee = z.infer<typeof NetworkFeeSchema>;

export const BuildSwapResultSchema = z.object({
  swapId: z.string(),
  txData: z.unknown(),
  networkFee: NetworkFeeSchema.nullable().catch(null),
});

export type BuildSwapResult = z.infer<typeof BuildSwapResultSchema>;

export const ConfirmSwapSchema = z.object({
  id: z.string(),
  status: z.string(),
  txHash: z.string(),
  network: z.string(),
  fromToken: z.string(),
  toToken: z.string(),
  fromAmount: AmountRawSchema,
  toAmount: z.string().nullable(),
  commissionAmount: z.string(),
});

export type ConfirmSwap = z.infer<typeof ConfirmSwapSchema>;

export const PriceSchema = z.object({
  provider: z.string(),
  priceUsd: z.string(),
  priceNative: z.string(),
  liquidity: z.string().optional(),
  volume24h: z.string().optional(),
});

export type Price = z.infer<typeof PriceSchema>;

export interface PriceParams {
  tokenAddress: string;
  network: string;
  pairAddress?: string;
}

export interface CrossChainQuoteParams {
  fromNetwork: string;
  toNetwork: string;
  fromToken: string;
  toToken: string;
  amount: string;
  userAddress: string;
  slippageBps?: number;
}

export const CrossChainQuoteSchema = z.object({
  provider: z.string(),
  toAmount: AmountRawSchema,
  minToAmount: AmountRawSchema,
  requiredSlippageBps: z.number().int(),
  feeBps: z.number().int(),
  estimatedTimeSec: z.number(),
});

export type CrossChainQuote = z.infer<typeof CrossChainQuoteSchema>;

export const CrossChainBuildSchema = CrossChainQuoteSchema.extend({
  swapId: z.string(),
  expiresAt: z.string(),
  networkFee: NetworkFeeSchema.nullable().catch(null),
  extraNativeFee: AmountRawSchema.catch(0n),
});

export type CrossChainBuild = z.infer<typeof CrossChainBuildSchema>;

export const CrossChainSwapStatusSchema = z.enum([
  'pending',
  'bridging',
  'completed',
  'failed',
  'refunded',
]);

export type CrossChainSwapStatus = z.infer<typeof CrossChainSwapStatusSchema>;

export const CrossChainSubmitSchema = z.object({
  status: CrossChainSwapStatusSchema,
  txHash: z.string(),
});

export type CrossChainSubmit = z.infer<typeof CrossChainSubmitSchema>;

const CrossChainRefundSchema = z.object({
  network: z.string(),
  token: z.string(),
  amount: z.string(),
  txHash: z.string(),
});

export const CrossChainSwapSchema = z.object({
  id: z.string(),
  status: CrossChainSwapStatusSchema,
  fromNetwork: z.string(),
  toNetwork: z.string(),
  fromToken: z.string(),
  toToken: z.string(),
  fromAmount: AmountRawSchema,
  toAmount: AmountRawSchema.nullable(),
  provider: z.string(),
  txHash: z.string().nullable(),
  destTxHash: z.string().nullable(),
  failureReason: z.string().nullable(),
  refund: CrossChainRefundSchema.nullable(),
  estimatedTimeSec: z.number().nullable(),
  createdAt: z.string(),
});

export type CrossChainSwap = z.infer<typeof CrossChainSwapSchema>;

export const CrossChainEventSchema = z.object({
  eventId: z.string().nullable(),
  swapId: z.string(),
  fromNetwork: z.string(),
  toNetwork: z.string(),
  txHash: z.string(),
  destTxHash: z.string().nullable(),
  failureReason: z.string().nullable(),
  refund: CrossChainRefundSchema.nullable(),
});

export type CrossChainEvent = z.infer<typeof CrossChainEventSchema>;
